import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { Building2, MessageCircle, Send, User } from "lucide-react";
import { API, portalHeaders } from "../helpers.js";

const QA_STATUS = {
  ouvert: { label: "Ouvert", bg: "bg-blue-100", text: "text-blue-700" },
  attente_client: { label: "En attente de vous", bg: "bg-amber-100", text: "text-amber-700" },
  attente_cabinet: { label: "En attente du cabinet", bg: "bg-orange-100", text: "text-orange-700" },
  repondu: { label: "Repondu", bg: "bg-green-100", text: "text-green-700" },
  ferme: { label: "Ferme", bg: "bg-gray-100", text: "text-gray-500" },
};

export default function QuestionsTab({ questions, selectedYear, primaryColor, onReload }) {
  const [selectedQ, setSelectedQ] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [newSubject, setNewSubject] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedQ) {
      const fresh = questions.find(q => q.id === selectedQ.id);
      if (fresh) setSelectedQ(fresh);
    }
  }, [questions, selectedQ]);

  // Batch #14.180 — Marque les messages du cabinet comme LUS dès qu'on ouvre une conversation
  useEffect(() => {
    if (!selectedQ?.id) return;
    const hasUnreadFromCabinet = (selectedQ.messages || []).some(
      (m) => m.author === "cabinet" && !m.read_at,
    );
    if (!hasUnreadFromCabinet) return;
    axios.post(`${API}/portal/questions/${selectedQ.id}/mark-read`, {}, { headers: portalHeaders() })
      .then(() => { onReload?.(); })
      .catch(() => { /* silent, best-effort */ });
  }, [selectedQ?.id, onReload]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Batch #14.181 — Typing indicator : poll toutes les 3s + heartbeat sur keystroke
  const [cabinetTyping, setCabinetTyping] = useState(false);
  const lastTypingPostRef = useRef(0);

  useEffect(() => {
    if (!selectedQ?.id) {
      setCabinetTyping(false);
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/portal/questions`, { headers: portalHeaders() });
        if (cancelled) return;
        const fresh = (r.data || []).find((q) => q.id === selectedQ.id);
        const until = fresh?.typing_cabinet_until;
        if (until) {
          const ms = new Date(until).getTime() - Date.now();
          setCabinetTyping(ms > 0);
        } else {
          setCabinetTyping(false);
        }
      } catch { /* silent */ }
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [selectedQ?.id]);

  const notifyTyping = useCallback(() => {
    if (!selectedQ?.id) return;
    const now = Date.now();
    if (now - lastTypingPostRef.current < 2500) return;  // throttle 1 POST / 2.5s
    lastTypingPostRef.current = now;
    axios.post(`${API}/portal/questions/${selectedQ.id}/typing`, {}, { headers: portalHeaders() })
      .catch(() => { /* silent */ });
  }, [selectedQ?.id]);

  const sendReply = async () => {
    if (!selectedQ || !replyText.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/portal/questions/${selectedQ.id}/reply`,
        { message: replyText }, { headers: portalHeaders() });
      setSelectedQ(res.data);
      setReplyText("");
      toast.success("Reponse envoyee");
      onReload();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  const createQuestion = async () => {
    if (!newSubject.trim() || !newMessage.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/portal/questions`,
        { subject: newSubject, message: newMessage, tax_year: selectedYear },
        { headers: portalHeaders() });
      toast.success("Question envoyee");
      setShowNew(false); setNewSubject(""); setNewMessage("");
      setSelectedQ(res.data);
      onReload();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  const pending = questions.filter(q => q.status === "attente_client");
  const others = questions.filter(q => q.status !== "attente_client");
  const allSorted = [...pending, ...others];

  return (
    <div className="space-y-4" data-testid="tab-questions">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-700">{questions.length} conversation(s)</p>
          {pending.length > 0 && <p className="text-xs text-amber-600 font-medium">{pending.length} en attente de votre reponse</p>}
        </div>
        <button onClick={() => { setShowNew(!showNew); setSelectedQ(null); }}
          className="text-white px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 font-medium"
          style={{ backgroundColor: primaryColor }} data-testid="portal-qa-new-btn">
          <MessageCircle className="w-3 h-3" /> Poser une question
        </button>
      </div>

      {showNew && (
        <div className="bg-white rounded-xl shadow-sm border p-4 space-y-3" data-testid="portal-qa-new-form">
          <h4 className="font-semibold text-sm">Nouvelle question à l'équipe</h4>
          <input type="text" value={newSubject} onChange={e => setNewSubject(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Sujet de votre question"
            maxLength={200} data-testid="portal-qa-new-subject" />
          <textarea value={newMessage} onChange={e => setNewMessage(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm h-20 resize-none"
            placeholder="Ecrivez votre question..." maxLength={2000} data-testid="portal-qa-new-message" />
          <div className="flex gap-2">
            <button onClick={() => setShowNew(false)} className="border px-3 py-1.5 rounded-lg text-sm">Annuler</button>
            <button onClick={createQuestion} disabled={loading || !newSubject.trim() || !newMessage.trim()}
              className="text-white px-4 py-1.5 rounded-lg text-sm disabled:opacity-50"
              style={{ backgroundColor: primaryColor }} data-testid="portal-qa-send-btn">
              {loading ? "..." : "Envoyer"}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2 space-y-2 max-h-[60vh] overflow-y-auto" data-testid="portal-qa-list">
          {allSorted.map(q => {
            const st = QA_STATUS[q.status] || QA_STATUS.ouvert;
            const isPending = q.status === "attente_client";
            const active = selectedQ?.id === q.id;
            const lastMsg = q.messages?.[q.messages.length - 1];
            const msgCount = q.messages?.length || 0;
            return (
              <button key={q.id} onClick={() => { setSelectedQ(q); setReplyText(""); setShowNew(false); }}
                className={`w-full text-left rounded-xl border p-3 transition hover:shadow-md ${active ? "ring-2 border-transparent shadow-md" : ""} ${isPending ? "bg-white border-amber-200" : "bg-white/60 border-gray-100"}`}
                style={active ? { borderColor: primaryColor, boxShadow: `0 0 0 2px ${primaryColor}` } : {}}
                data-testid={`portal-qa-${q.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className={`text-sm truncate ${isPending ? "font-bold" : "font-medium"}`}>{q.subject}</p>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${st.bg} ${st.text} font-medium`}>{st.label}</span>
                </div>
                {lastMsg && <p className="text-xs text-gray-500 mt-1 truncate">{lastMsg.author === "cabinet" ? "Équipe" : "Vous"}: {lastMsg.message}</p>}
                <div className="flex items-center justify-between mt-1">
                  <p className="text-[10px] text-gray-400">{new Date(q.updated_at).toLocaleDateString("fr-CA")}</p>
                  <p className="text-[10px] text-gray-400">{msgCount} message{msgCount > 1 ? "s" : ""}</p>
                </div>
              </button>
            );
          })}
          {!questions.length && !showNew && (
            <div className="bg-white rounded-xl shadow-sm border p-8 text-center text-gray-400">
              <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Aucune question pour cette annee</p>
              <p className="text-xs mt-1">Cliquez "Poser une question" pour commencer</p>
            </div>
          )}
        </div>

        <div className="lg:col-span-3">
          {selectedQ ? (
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden flex flex-col" style={{ maxHeight: "60vh" }} data-testid="portal-qa-conversation">
              <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-sm">{selectedQ.subject}</h4>
                  <p className="text-xs text-gray-500">{selectedQ.tax_year} — {QA_STATUS[selectedQ.status]?.label}</p>
                </div>
                <button onClick={() => setSelectedQ(null)} className="text-gray-400 hover:text-gray-600 text-xs px-2 py-1 border rounded-lg">Fermer</button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {(() => {
                  const msgs = selectedQ.messages || [];
                  // Batch #14.180 — Trouve l'INDEX du dernier message envoyé par MOI (client) pour afficher "Lu" / "Distribué" dessous
                  let lastOwnIdx = -1;
                  for (let i = msgs.length - 1; i >= 0; i--) {
                    if (msgs[i].author === "client") { lastOwnIdx = i; break; }
                  }
                  return msgs.map((msg, idx) => {
                    const isCabinet = msg.author === "cabinet";
                    const showReceipt = !isCabinet && idx === lastOwnIdx;
                    return (
                      <div key={msg.id} className={`flex flex-col ${isCabinet ? "items-start" : "items-end"}`}>
                        <div className={`max-w-[80%] rounded-xl px-3.5 py-2.5 ${isCabinet ? "bg-gray-100 text-gray-800" : "text-white"}`}
                          style={!isCabinet ? { backgroundColor: primaryColor } : {}}>
                          <div className="flex items-center gap-1.5 mb-1">
                            {isCabinet ? <Building2 className="w-3 h-3 text-gray-400" /> : <User className="w-3 h-3 opacity-70" />}
                            <span className={`text-[10px] font-medium ${isCabinet ? "text-gray-500" : "opacity-80"}`}>{msg.author_name}</span>
                            <span className={`text-[10px] ${isCabinet ? "text-gray-400" : "opacity-60"}`}>
                              {new Date(msg.created_at).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" })}
                            </span>
                          </div>
                          <p className="text-sm whitespace-pre-line">{msg.message}</p>
                        </div>
                        {showReceipt && (
                          <span className="text-[10px] text-gray-400 mt-0.5 mr-1" data-testid={`portal-qa-receipt-${msg.id}`}>
                            {msg.read_at ? "Lu" : "Distribué"}
                          </span>
                        )}
                      </div>
                    );
                  });
                })()}
                {cabinetTyping && (
                  <div className="flex items-center" data-testid="portal-qa-typing-indicator">
                    <div className="bg-gray-100 text-gray-500 rounded-xl px-3 py-2 inline-flex items-center gap-1.5 shadow-sm">
                      <span className="text-[10px] italic">L'équipe écrit</span>
                      <span className="flex gap-0.5">
                        <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                      </span>
                    </div>
                  </div>
                )}
              </div>
              {selectedQ.status !== "ferme" && (
                <div className="border-t p-3 flex gap-2">
                  <input type="text" value={replyText}
                    onChange={e => { setReplyText(e.target.value); notifyTyping(); }}
                    onKeyDown={e => e.key === "Enter" && sendReply()}
                    className="flex-1 border rounded-lg px-3 py-2 text-sm" placeholder="Ecrire une reponse..."
                    data-testid="portal-qa-reply-input" />
                  <button onClick={sendReply} disabled={loading || !replyText.trim()}
                    className="text-white px-3 py-2 rounded-lg text-sm disabled:opacity-50"
                    style={{ backgroundColor: primaryColor }} data-testid="portal-qa-reply-send">
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border p-12 text-center text-gray-400">
              <MessageCircle className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Cliquez sur une conversation pour voir les messages</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
